import { useEffect, useState, type ReactNode, type TransitionEvent } from "react";

/** Right-side slide-in drawer shell, mirroring the lead-drawer motion (slide in
 *  on mount; slide out then unmount on close). Used by the New Job and Task
 *  forms. While `busy` the overlay click + close button are inert so a save
 *  can't be interrupted mid-flight. */
export function FormDrawer({
  title,
  onClose,
  busy = false,
  footer,
  children,
  closeOnOverlayClick = true,
}: {
  title: string;
  onClose: () => void;
  busy?: boolean;
  footer: ReactNode;
  children: ReactNode;
  /** When false, a backdrop click does NOT close the drawer (only the ✕ does) —
   *  for drawers where an accidental dismiss would lose in-progress selections. */
  closeOnOverlayClick?: boolean;
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setOpen(true));
    return () => cancelAnimationFrame(id);
  }, []);

  function requestClose() {
    if (busy) return;
    setOpen(false);
  }
  function handleOverlayClick() {
    if (closeOnOverlayClick) requestClose();
  }
  function handleTransitionEnd(e: TransitionEvent) {
    if (e.target === e.currentTarget && e.propertyName === "transform" && !open) onClose();
  }

  return (
    <div className={`job-drawer__overlay${open ? " job-drawer__overlay--open" : ""}`} onClick={handleOverlayClick}>
      <aside
        className={`job-drawer${open ? " job-drawer--open" : ""}`}
        onClick={(e) => e.stopPropagation()}
        onTransitionEnd={handleTransitionEnd}
        role="dialog"
        aria-label={title}
      >
        <header className="job-drawer__head">
          <h2>{title}</h2>
          <button type="button" className="job-drawer__close" onClick={requestClose} disabled={busy} aria-label="Close">✕</button>
        </header>
        <div className="job-drawer__body">{children}</div>
        <footer className="job-drawer__foot">{footer}</footer>
      </aside>
    </div>
  );
}
